import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { SwipeAction } from '../hooks/useSwipeGestures';

export type SwipeActionType = 'like' | 'reply' | 'share' | 'bookmark' | 'report' | 'mute' | 'hide';

export interface SwipeActionConfig {
  type: SwipeActionType;
  label: string;
  icon: string;
  color: string;
  backgroundColor: string;
}

export interface SwipePreferences {
  leftAction: SwipeActionType;
  rightAction: SwipeActionType;
  isEnabled: boolean;
  threshold: number;
  maxThreshold: number;
}

interface SwipePreferencesContextType {
  preferences: SwipePreferences;
  updatePreferences: (newPreferences: Partial<SwipePreferences>) => void;
  getSwipeAction: (type: SwipeActionType, onAction: () => void) => SwipeAction;
  availableActions: SwipeActionConfig[];
}

const defaultPreferences: SwipePreferences = {
  leftAction: 'reply',
  rightAction: 'like',
  isEnabled: true,
  threshold: 100,
  maxThreshold: 200
};

const availableActions: SwipeActionConfig[] = [
  {
    type: 'like',
    label: 'Liker',
    icon: '❤️',
    color: '#ffffff',
    backgroundColor: 'rgba(239, 68, 68, 0.8)'
  },
  {
    type: 'reply',
    label: 'Répondre',
    icon: '💬',
    color: '#ffffff',
    backgroundColor: 'rgba(59, 130, 246, 0.8)'
  },
  {
    type: 'share',
    label: 'Partager',
    icon: '📤',
    color: '#ffffff',
    backgroundColor: 'rgba(16, 185, 129, 0.8)'
  },
  {
    type: 'bookmark',
    label: 'Sauvegarder',
    icon: '🔖',
    color: '#ffffff',
    backgroundColor: 'rgba(245, 158, 11, 0.8)'
  },
  {
    type: 'report',
    label: 'Signaler',
    icon: '🚩',
    color: '#ffffff',
    backgroundColor: 'rgba(220, 38, 38, 0.8)'
  },
  {
    type: 'mute',
    label: 'Masquer l\'auteur',
    icon: '🔇',
    color: '#ffffff',
    backgroundColor: 'rgba(107, 114, 128, 0.8)'
  },
  {
    type: 'hide',
    label: 'Masquer le post',
    icon: '👁️‍🗨️',
    color: '#ffffff',
    backgroundColor: 'rgba(75, 85, 99, 0.8)'
  }
];

const SwipePreferencesContext = createContext<SwipePreferencesContextType | undefined>(undefined);

export const useSwipePreferences = (): SwipePreferencesContextType => {
  const context = useContext(SwipePreferencesContext);
  if (!context) {
    throw new Error('useSwipePreferences must be used within a SwipePreferencesProvider');
  }
  return context;
};

interface SwipePreferencesProviderProps {
  children: ReactNode;
}

export const SwipePreferencesProvider: React.FC<SwipePreferencesProviderProps> = ({ children }) => {
  const [preferences, setPreferences] = useState<SwipePreferences>(defaultPreferences);

  // Charger les préférences depuis localStorage
  useEffect(() => {
    const savedPreferences = localStorage.getItem('swipePreferences');
    if (savedPreferences) {
      try {
        const parsed = JSON.parse(savedPreferences);
        setPreferences({ ...defaultPreferences, ...parsed });
      } catch (error) {
        console.error('Erreur lors du chargement des préférences de swipe:', error);
      }
    }
  }, []);

  // Sauvegarder les préférences dans localStorage
  const updatePreferences = (newPreferences: Partial<SwipePreferences>) => {
    const updated = { ...preferences, ...newPreferences };
    setPreferences(updated);
    localStorage.setItem('swipePreferences', JSON.stringify(updated));
  };

  // Convertir un type d'action en SwipeAction avec callback
  const getSwipeAction = (type: SwipeActionType, onAction: () => void): SwipeAction => {
    const config = availableActions.find(action => action.type === type);
    if (!config) {
      throw new Error(`Action type "${type}" not found`);
    }

    return {
      id: type,
      label: config.label,
      icon: config.icon,
      color: config.color,
      backgroundColor: config.backgroundColor,
      action: onAction
    };
  };

  const value: SwipePreferencesContextType = {
    preferences,
    updatePreferences,
    getSwipeAction,
    availableActions
  };

  return (
    <SwipePreferencesContext.Provider value={value}>
      {children}
    </SwipePreferencesContext.Provider>
  );
};
